import type { DateTimeFilterBounds } from '@/shared/lib/dateTimeFilter';
import { useRecordActions } from '@/features/RecordActions/model/useRecordActions';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProblemAddressStore } from '@/entities/ProblemAddress/model/store';
import { problemAddressFiltersToQuery } from '@/entities/ProblemAddress/mappers';
import { COORDINATE_QUALITY_OPTIONS, PROBLEM_ADDRESS_COLUMNS } from '@/entities/ProblemAddress/config/problem-address.config';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { EntityActionPanel } from '@/shared/ui/EntityActionPanel/EntityActionPanel';
import { InputText } from '@/shared/ui/InputText/InputText';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { RequestErrorBanner } from '@/shared/ui/RequestState';
import { DropdownField } from '@/shared/ui/DropdownField/DropdownField';
import classes from './ProblemAddressesPage.module.scss';

/** Показывает очередь проверки координат домов */
export const ProblemAddressesPage = () => {
    const recordActions = useRecordActions('problemAddresses', () => refresh());

    const navigate = useNavigate();
    const [verified, setVerified] = useState(false);
    const [search, setSearch] = useState('');
    const [city, setCity] = useState('');
    const [quality, setQuality] = useState('');
    const [dateRange, setDateRange] = useState<DateTimeFilterBounds>({});
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(25);

    const [query, setQuery] = useState({
        search: '',
        city: ''
    });

    const result = useProblemAddressStore((state) => state.list);
    const status = useProblemAddressStore((state) => state.listStatus);
    const error = useProblemAddressStore((state) => state.listError);
    const fetchList = useProblemAddressStore((state) => state.fetchList);
    const loading = status === RequestStatus.Loading;
    const [refreshRevision, setRefreshRevision] = useState(0);
    const refresh = useCallback(() => setRefreshRevision((revision) => revision + 1), []);

    useEffect(() => {
        if (search === query.search && city === query.city) return;

        const timer = window.setTimeout(() => {
            setFirst(0);

            setQuery({
                search,
                city
            });
        }, 300);

        return () => window.clearTimeout(timer);
    }, [search, city, query]);

    useEffect(() => {
        const controller = new AbortController();

        void fetchList(problemAddressFiltersToQuery({
            verifiedFrom: dateRange.from,
            verifiedTo: dateRange.to,
            verified,
            ...query,
            quality,
            first,
            rows
        }), controller.signal);

        return () => controller.abort();
    }, [verified, query, quality, first, rows, refreshRevision, fetchList, dateRange]);

    return <div className={classes.page}>
        <EntityActionPanel
            name="Проблемные адреса"
            buttons={
                <ActionButton
                    label="Обновить"
                    icon="pi pi-refresh"
                    onClick={refresh}
                    loading={loading}
                />
            }
        >
            <p>Проверка координат домов. Каждый дом показан по своей точке, без повторов снимков заказов.</p>
        </EntityActionPanel>
        <div className={classes.toolbar}>
            <ActionButton
                label="Требуют проверки"
                outlined={verified}
                aria-pressed={!verified}
                onClick={() => {
                    setVerified(false);
                    setFirst(0);
                }}
            />
            <ActionButton
                label="Проверенные"
                outlined={!verified}
                aria-pressed={verified}
                onClick={() => {
                    setVerified(true);
                    setFirst(0);
                }}
            />
            <InputText
                name="addressSearch"
                label="Адрес"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Улица, дом"
            />
            <InputText
                name="addressCity"
                label="Город"
                value={city}
                onChange={(event) => setCity(event.target.value)}
            />
            <DropdownField
                name="coordinateQuality"
                label="Исходная точность"
                value={quality}
                options={COORDINATE_QUALITY_OPTIONS}
                optionValue="value"
                optionLabel="label"
                showClear={false}
                onChange={(event) => {
                    setQuality(typeof event.value === 'string'
                        ? event.value
                        : '');

                    setFirst(0);
                }}
            />
        </div>
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить адреса"
            onRetry={refresh}
        />
        <DataTable
            onDateFilterChange={(_field, bounds) => {
                setDateRange(bounds ?? {});
                setFirst(0);
            }}
            rowActions={recordActions.getMenuItems}
            getRowUrl={item => item.id
                ? `/problem-addresses/${encodeURIComponent(item.id)}`
                : undefined}
            data={result.addresses ?? []}
            columns={PROBLEM_ADDRESS_COLUMNS}
            loading={loading}
            onRowClick={(point) => point.id && navigate(`/problem-addresses/${point.id}`)}
            paginator
            lazy
            first={first}
            rows={rows}
            totalRecords={result.allCount ?? 0}
            onPageChange={(event) => {
                setFirst(event.first);
                setRows(event.rows);
            }}
            emptyMessage={verified
                ? 'Проверенных домов пока нет'
                : 'Адресов для проверки нет'
            }
        />
        {recordActions.dialogs}
    </div>;
};
